// Import react elements
import { useContext, useEffect, useMemo, useState } from "react";

// Import strich api calls
import {
    getTerms,
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";
import { useToast } from "global/hooks/useToast";
import { UseScrollingSections } from "global/hooks/useScrollingSections";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
// Import policy page components.
import { PolicyNavigation } from "pages/landing_page/policies/components/PolicyNavigation";
import { PolicySection } from "pages/landing_page/policies/components/PolicySection";
import { BackToTopButton } from "global/components/BackToTopButton";

// Import css stylesheet
import "./TermsAndConditions.css";



// AccountPage.js
const TermsAndConditions = ({documentType}) => {


    // Send in 'document type' to get either 'terms and conditions' or 'privacy notice' data


    // Set title inside the page
    usePageTitle(documentType === "terms" ? "Terms & Conditions" : "Privacy Notice");

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Set loading state
    const [loading, setLoading] = useState(false);

    // Set state for storing t&cs
    const [terms, setTerms] = useState([]);

    // Define toast options
    const { toastError } = useToast();



    // Function to fetch the terms and conditions
    const getTandCs = async () => {

        // Set loading state
        setLoading(true);

        // Make the call
        const response = await getTerms(token, documentType);

        if (response.success) {

            // Update state
            setTerms(response.results);

            // Clear loading 
            setLoading(false);

        } else {

            // Set error message
            const errorMessage = response.error_message ? response.error_message : "Oops something went wrong."

            // Toast error
            toastError({ title: "Error", message: "Could not fetch T&Cs: " + errorMessage, autoCloseMs: 6000 });

        }

    }



    // Use effect to run when canonical products updates/loads
    useEffect(() => {

        // When canonical products loads check if this is an 'addnew' design if so we want to set all products active by default when adding a new design
        if (!token)  return ;

        // Get the t&cs
        getTandCs();
       
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, documentType]);



    // Sort the policy sections into their API-defined order.
    const sortedPolicies = useMemo(() => {

        // Return an empty array when policies have not loaded.
        if (!Array.isArray(terms)) {

            // Return the safe fallback.
            return [];

        }

        // Copy and sort the policies without mutating API data.
        return [...terms].sort((firstPolicy, secondPolicy) => {

            // Sort using the API ordering value.
            return Number(firstPolicy?.ordering ?? 0) - Number(secondPolicy?.ordering ?? 0);

        });

    }, [terms]);



    // Set up reusable section scrolling behaviour.
    const {
        activeSectionKey,
        handleSectionSelect,
        navigationScrollRef,
    } = UseScrollingSections({

        // Supply the policy sections.
        sections: sortedPolicies,

        // Tell the hook how to get a unique section key.
        getSectionKey: (policy) => policy?.section_key,

    });






    // If userData is missing or empty then show a loading state
    if (!token || loading) {

        return (

            <Grid>
            
                <GridItem>

                    <div className="account-loading">

                        <LoadingSpinner message="Loading..." />
                        
                    </div>
                    
                </GridItem>

            </Grid>
            
        );

    }



    return (

        <>

            {/* Render the navigation and policy content layout. */}
            <div className="terms-policy-page__layout">

                {/* Render the policy section navigation. */}
                <PolicyNavigation
                    policies={sortedPolicies}
                    activeSectionKey={activeSectionKey}
                    onSectionSelect={handleSectionSelect}
                    navigationScrollRef={navigationScrollRef}
                />

                {/* Render all policy sections. */}
                <div className="policy-page__content">

                    {/* Loop through each policy section. */}
                    {sortedPolicies.map((policy) => (

                        // Render the individual policy section.
                        <PolicySection
                            key={policy?.section_key}
                            policy={policy}
                        />

                    ))}

                </div>

            </div>
 
             {/* Render the floating back-to-top button. */}
            <BackToTopButton />

        </>
        
    );

};

export default TermsAndConditions;